/**
 * Service for managing flag types in Firestore.
 * Flag types define school-defined labels that can be assigned to students
 * (e.g., "Student Council", "Counselling", "Serious Allergy").
 *
 * Mirrors the MentionTypesService class pattern.
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase.js';

class FlagTypesService {
  constructor() {
    this.collectionName = 'flagTypes';
    // Cache is keyed by schoolId: Map<schoolId, { data, timestamp }>
    this._cache = new Map();
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get collection reference for a school's flagTypes subcollection.
   * @param {string} schoolId - School ID (required)
   * @returns {CollectionReference}
   * @throws {Error} if schoolId is absent
   */
  getCollectionRef(schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for flagTypes collection');
    }
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  /**
   * Check whether the cache for a given school is still valid.
   * @param {string} schoolId
   * @returns {boolean}
   */
  _isCacheValid(schoolId) {
    const entry = this._cache.get(schoolId);
    if (!entry) return false;
    return Date.now() - entry.timestamp < this.cacheDuration;
  }

  /**
   * Clear the in-memory cache (all schools).
   */
  clearCache() {
    this._cache.clear();
  }

  /**
   * Get all active flag types for a school, with 5-minute caching.
   * @param {string} schoolId
   * @returns {Promise<FlagType[]>}
   */
  async getAll(schoolId) {
    if (this._isCacheValid(schoolId)) {
      return this._cache.get(schoolId).data;
    }

    const q = query(this.getCollectionRef(schoolId), orderBy('name'));
    const snapshot = await getDocs(q);

    const types = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(ft => ft.active !== false);

    this._cache.set(schoolId, { data: types, timestamp: Date.now() });
    return types;
  }

  /**
   * Fetch multiple flag types by their IDs in parallel.
   * Documents that don't exist are silently skipped.
   * @param {string} schoolId
   * @param {string[]} flagIds
   * @returns {Promise<FlagType[]>}
   */
  async getByIds(schoolId, flagIds) {
    if (!flagIds || flagIds.length === 0) return [];

    const snaps = await Promise.all(
      flagIds.map(id =>
        getDoc(doc(db, 'schools', schoolId, this.collectionName, id))
      )
    );

    return snaps
      .filter(snap => snap.exists())
      .map(snap => ({ id: snap.id, ...snap.data() }));
  }

  /**
   * Create a new flag type.
   * @param {string} schoolId
   * @param {Object} data - { name, colour, notificationTargetId, notificationTargetName, createdBy }
   * @returns {Promise<FlagType>} The created flag type with its id
   */
  async create(schoolId, data) {
    const docData = {
      ...data,
      active: true,
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(this.getCollectionRef(schoolId), docData);
    this.clearCache();

    return { id: docRef.id, ...docData };
  }

  /**
   * Update an existing flag type.
   * @param {string} schoolId
   * @param {string} flagTypeId
   * @param {Object} data - Partial flag type data to update
   * @returns {Promise<void>}
   */
  async update(schoolId, flagTypeId, data) {
    const docRef = doc(db, 'schools', schoolId, this.collectionName, flagTypeId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
    this.clearCache();
  }

  /**
   * Hard-delete a flag type document.
   * @param {string} schoolId
   * @param {string} flagTypeId
   * @returns {Promise<void>}
   */
  async remove(schoolId, flagTypeId) {
    const docRef = doc(db, 'schools', schoolId, this.collectionName, flagTypeId);
    await deleteDoc(docRef);
    this.clearCache();
  }
}

export const flagTypesService = new FlagTypesService();
export default flagTypesService;
